import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Build as ToolIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

import { RootState } from '../../store';
import {
  fetchToolFixtureByIdStart,
  fetchDropdownsStart,
  createToolFixtureStart,
  updateToolFixtureStart,
  clearError,
  setValidationErrors,
  clearValidationErrors,
  resetToolFixtureState,
} from '../../store/reducers/toolFixtureSlice';
import { CreateToolFixtureRequest, UpdateToolFixtureRequest, toolFixtureApi } from '../../services/toolFixtureApi';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 1200,
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  headerIcon: {
    marginRight: theme.spacing(2),
    fontSize: 32,
    color: theme.palette.primary.main,
  },
  title: {
    fontWeight: 'bold',
  },
  card: {
    marginBottom: theme.spacing(3),
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  cardIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  formControl: {
    marginBottom: theme.spacing(2),
  },
  textField: {
    marginBottom: theme.spacing(2),
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(3),
    padding: theme.spacing(2, 0),
  },
  leftActions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  rightActions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  validationErrors: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
    borderRadius: theme.shape.borderRadius,
  },
  calibrationNote: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: '#fff3e0',
    borderRadius: theme.shape.borderRadius,
    fontSize: '0.875rem',
    color: '#e65100',
  },
}));

const ToolFixtureEdit: React.FC = () => {
  const classes = useStyles();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isEditMode = Boolean(id);

  const {
    selectedToolFixture,
    selectedToolFixtureLoading,
    suppliers,
    departments,
    toolTypes,
    dropdownsLoading,
    createToolFixtureLoading,
    updateToolFixtureLoading,
    createToolFixtureError,
    updateToolFixtureError,
    validationErrors,
  } = useSelector((state: RootState) => state.toolFixture);

  // Form state
  const [formData, setFormData] = useState<CreateToolFixtureRequest>({
    TFNum: '',
    Description: '',
    SupplierId: '',
    PricePerUnit: 0,
    DepartmentId: '',
    LeadTime: '',
    MinStockLevel: 0,
    MinOrderQty: 1,
    CalibrationRequired: false,
    ToolTypeId: '',
    SerialNum: '',
    Active: true,
  });

  useEffect(() => {
    dispatch(resetToolFixtureState());
    dispatch(fetchDropdownsStart());
    
    if (isEditMode && id) {
      dispatch(fetchToolFixtureByIdStart(id));
    }

    return () => {
      dispatch(clearValidationErrors());
      dispatch(clearError());
    };
  }, [dispatch, isEditMode, id]);

  useEffect(() => {
    if (selectedToolFixture && isEditMode) {
      setFormData({
        TFNum: selectedToolFixture.TFNum,
        Description: selectedToolFixture.Description,
        SupplierId: selectedToolFixture.SupplierId,
        PricePerUnit: selectedToolFixture.PricePerUnit,
        DepartmentId: selectedToolFixture.DepartmentId,
        LeadTime: selectedToolFixture.LeadTime,
        MinStockLevel: selectedToolFixture.MinStockLevel,
        MinOrderQty: selectedToolFixture.MinOrderQty,
        CalibrationRequired: selectedToolFixture.CalibrationRequired,
        ToolTypeId: selectedToolFixture.ToolTypeId,
        SerialNum: selectedToolFixture.SerialNum || '',
        Active: selectedToolFixture.Active,
      });
    }
  }, [selectedToolFixture, isEditMode]);

  const handleInputChange = (field: keyof CreateToolFixtureRequest) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: field === 'PricePerUnit' || field === 'MinStockLevel' || field === 'MinOrderQty'
        ? parseFloat(value) || 0
        : value,
    }));
  };

  const handleSelectChange = (field: keyof CreateToolFixtureRequest) => (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleCheckboxChange = (field: keyof CreateToolFixtureRequest) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.checked,
    }));
  };

  const handleSave = async () => {
    dispatch(clearValidationErrors());

    // Client-side validation
    const errors = toolFixtureApi.validateToolFixture(formData);
    if (errors.length > 0) {
      dispatch(setValidationErrors(errors));
      return;
    }

    if (isEditMode && id) {
      const updateData: UpdateToolFixtureRequest = {
        Id: id,
        ...formData,
      };
      dispatch(updateToolFixtureStart(updateData));
    } else {
      dispatch(createToolFixtureStart(formData));
    }
  };

  const handleCancel = () => {
    navigate('/tools-fixtures');
  };

  const handleErrorClose = () => {
    dispatch(clearError());
  };

  // Get display names for dropdowns
  const getSupplierName = (supplierId: string) => {
    const supplier = suppliers.find(s => s.Id === supplierId);
    return supplier ? supplier.Name : '';
  };

  const getDepartmentName = (departmentId: string) => {
    const department = departments.find(d => d.Id === departmentId);
    return department ? department.Name : '';
  };

  const getToolTypeName = (toolTypeId: string) => {
    const toolType = toolTypes.find(t => t.Id === toolTypeId);
    return toolType ? toolType.Name : '';
  };

  // Navigate to success page after successful creation/update
  useEffect(() => {
    if (!createToolFixtureLoading && !updateToolFixtureLoading && 
        !createToolFixtureError && !updateToolFixtureError && 
        (selectedToolFixture || !isEditMode)) {
      // Small delay to show success before navigating
      const timer = setTimeout(() => {
        navigate('/tools-fixtures');
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [
    createToolFixtureLoading,
    updateToolFixtureLoading,
    createToolFixtureError,
    updateToolFixtureError,
    selectedToolFixture,
    isEditMode,
    navigate,
  ]);

  if (dropdownsLoading || (isEditMode && selectedToolFixtureLoading)) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  const currentError = createToolFixtureError || updateToolFixtureError;
  const currentLoading = createToolFixtureLoading || updateToolFixtureLoading;

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <ToolIcon className={classes.headerIcon} />
        <Typography variant="h4" className={classes.title}>
          {isEditMode ? 'Edit Tool/Fixture' : 'Add New Tool/Fixture'}
        </Typography>
      </Box>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <Box className={classes.validationErrors}>
          <Typography variant="subtitle2" gutterBottom>
            Please fix the following errors:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Box>
      )}

      {/* Basic Information Card */}
      <Card className={classes.card}>
        <CardContent>
          <Box className={classes.cardHeader}>
            <ToolIcon className={classes.cardIcon} />
            <Typography variant="h6">Basic Information</Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="TF Number *"
                value={formData.TFNum}
                onChange={handleInputChange('TFNum')}
                className={classes.textField}
                placeholder="Enter tool/fixture number"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Serial Number"
                value={formData.SerialNum}
                onChange={handleInputChange('SerialNum')}
                className={classes.textField}
                placeholder="Enter serial number (optional)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                label="Description *"
                value={formData.Description}
                onChange={handleInputChange('Description')}
                className={classes.textField}
                placeholder="Enter tool/fixture description"
                multiline
                rows={3}
                required
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Classification Card */}
      <Card className={classes.card}>
        <CardContent>
          <Box className={classes.cardHeader}>
            <ToolIcon className={classes.cardIcon} />
            <Typography variant="h6">Classification</Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined" className={classes.formControl} required>
                <InputLabel>Supplier *</InputLabel>
                <Select
                  value={formData.SupplierId}
                  onChange={handleSelectChange('SupplierId')}
                  label="Supplier *"
                >
                  <MenuItem value="">
                    <em>Select a supplier</em>
                  </MenuItem>
                  {suppliers.map((supplier) => (
                    <MenuItem key={supplier.Id} value={supplier.Id}>
                      {supplier.Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined" className={classes.formControl} required>
                <InputLabel>Department *</InputLabel>
                <Select
                  value={formData.DepartmentId}
                  onChange={handleSelectChange('DepartmentId')}
                  label="Department *"
                >
                  <MenuItem value="">
                    <em>Select a department</em>
                  </MenuItem>
                  {departments.map((department) => (
                    <MenuItem key={department.Id} value={department.Id}>
                      {department.Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined" className={classes.formControl} required>
                <InputLabel>Tool Type *</InputLabel>
                <Select
                  value={formData.ToolTypeId}
                  onChange={handleSelectChange('ToolTypeId')}
                  label="Tool Type *"
                >
                  <MenuItem value="">
                    <em>Select a tool type</em>
                  </MenuItem>
                  {toolTypes.map((toolType) => (
                    <MenuItem key={toolType.Id} value={toolType.Id}>
                      {toolType.Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Financial & Inventory Card */}
      <Card className={classes.card}>
        <CardContent>
          <Box className={classes.cardHeader}>
            <ToolIcon className={classes.cardIcon} />
            <Typography variant="h6">Financial & Inventory</Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Price Per Unit"
                type="number"
                value={formData.PricePerUnit}
                onChange={handleInputChange('PricePerUnit')}
                className={classes.textField}
                placeholder="0.00"
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Lead Time (Days)"
                value={formData.LeadTime}
                onChange={handleInputChange('LeadTime')}
                className={classes.textField}
                placeholder="Enter lead time in days"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Minimum Stock Level"
                type="number"
                value={formData.MinStockLevel}
                onChange={handleInputChange('MinStockLevel')}
                className={classes.textField}
                placeholder="0"
                inputProps={{ min: 0, step: 1 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Minimum Order Quantity *"
                type="number"
                value={formData.MinOrderQty}
                onChange={handleInputChange('MinOrderQty')}
                className={classes.textField}
                placeholder="1"
                inputProps={{ min: 1, step: 1 }}
                required
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Settings Card */}
      <Card className={classes.card}>
        <CardContent>
          <Box className={classes.cardHeader}>
            <ToolIcon className={classes.cardIcon} />
            <Typography variant="h6">Settings</Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.CalibrationRequired}
                    onChange={handleCheckboxChange('CalibrationRequired')}
                    color="primary"
                  />
                }
                label="Calibration Required"
              />
              {formData.CalibrationRequired && (
                <Box className={classes.calibrationNote}>
                  <Typography variant="body2">
                    Note: Enabling calibration will allow you to schedule maintenance events for this tool/fixture.
                  </Typography>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.Active}
                    onChange={handleCheckboxChange('Active')}
                    color="primary"
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Card - Show in edit mode */}
      {isEditMode && selectedToolFixture && (
        <Card className={classes.card}>
          <CardContent>
            <Box className={classes.cardHeader}>
              <ToolIcon className={classes.cardIcon} />
              <Typography variant="h6">Summary</Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  TF Number: <strong>{formData.TFNum}</strong>
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  Supplier: <strong>{getSupplierName(formData.SupplierId)}</strong>
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  Department: <strong>{getDepartmentName(formData.DepartmentId)}</strong>
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  Tool Type: <strong>{getToolTypeName(formData.ToolTypeId)}</strong>
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  Price Per Unit: <strong>${formData.PricePerUnit.toFixed(2)}</strong>
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  Calibration Required: <strong>{formData.CalibrationRequired ? 'Yes' : 'No'}</strong>
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <Box className={classes.actions}>
        <Button
          variant="outlined"
          startIcon={<CancelIcon />}
          onClick={handleCancel}
          disabled={currentLoading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={currentLoading}
        >
          {currentLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            isEditMode ? 'Update Tool/Fixture' : 'Create Tool/Fixture'
          )}
        </Button>
      </Box>

      {/* Error Snackbar */}
      <Snackbar
        open={!!currentError}
        autoHideDuration={6000}
        onClose={handleErrorClose}
        message={currentError}
      />
    </Box>
  );
};

export default ToolFixtureEdit; 